import { GetCustomerLocationsQuery } from '@api/generated/graphql';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { graphql } from '@api/generated';

export const getCustomerLocationQuery = graphql(`
  query getCustomerLocations(
    $tenantId: ID!
    $customerId: ID!
    $skip: Int
    $take: Int
    $includeTotalCount: Boolean = false
  ) {
    customerLocations(
      tenantId: $tenantId
      customerId: $customerId
      skip: $skip
      take: $take
    ) {
      items {
        id
        name
        active
        address {
          street
          unit
          city
          state
          zip
          country
          latitude
          longitude
        }
      }
      totalCount @include(if: $includeTotalCount)
    }
  }
`);

interface CustomerLocationFetcherParams<THasTotalCount extends boolean> {
  tenantId: string;
  customerId: string;
  skip?: number;
  take?: number;
  includeTotalCount?: THasTotalCount;
}

export type CustomerLocation = NonNullable<
  NonNullable<GetCustomerLocationsQuery['customerLocations']>['items']
>[0];

export interface CustomerLocationFetcherResult<THasTotalCount extends boolean> {
  data: CustomerLocation[];
  skip: number;
  take: number;
  totalCount: THasTotalCount extends true ? number : undefined;
}

export type CustomerLocationFetcher = <THasTotalCount extends boolean = false>(
  params: CustomerLocationFetcherParams<THasTotalCount>,
) => Promise<CustomerLocationFetcherResult<THasTotalCount>>;

interface UseLazyCustomerLocationsQueryResult {
  fetchCustomerLocations: CustomerLocationFetcher;
  isLoading: boolean;
  lastError: ApolloError | undefined;
}

export const useLazyCustomerLocationsQuery =
  (): UseLazyCustomerLocationsQueryResult => {
    const [getCustomerLocations, { loading, error }] = useLazyQuery(
      getCustomerLocationQuery,
      {
        notifyOnNetworkStatusChange: true,
      },
    );

    // Function to fetch with dynamic pagination
    const fetchCustomerLocations = async <
      THasTotalCount extends boolean = false,
    >(
      params: CustomerLocationFetcherParams<THasTotalCount>,
    ): Promise<CustomerLocationFetcherResult<THasTotalCount>> => {
      const {
        tenantId,
        customerId,
        skip = 0,
        take = 200,
        includeTotalCount,
      } = params;

      const result = await getCustomerLocations({
        variables: { tenantId, customerId, skip, take, includeTotalCount },
      });

      return {
        data: result.data?.customerLocations?.items ?? [],
        skip,
        take,
        ...(includeTotalCount && {
          totalCount: result.data?.customerLocations?.totalCount ?? 0,
        }),
      } as CustomerLocationFetcherResult<THasTotalCount>;
    };

    return {
      fetchCustomerLocations,
      isLoading: loading,
      lastError: error,
    };
  };

export default useLazyCustomerLocationsQuery;
